import { faMinus, faPlus } from '@fortawesome/pro-regular-svg-icons'
import Grid from '@material-ui/core/Grid'
import { useRouter } from 'next/router'
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'
import { Accordion } from '../../../components/accordion/accordion'
import { TopicFilters, TopicFiltersProps } from '../../../components/topicfilters/TopicFilters'
import { html } from '../../../utils/htmlParser'
import { isNotNull, nonNull } from '../../../utils/nonNull'
import { trackFaqView } from '../../../utils/trackFaqView'
import { FaqProps } from '../index'
import {
  StyledContactCtaWrapper,
  StyledFAQHeader,
  StyledFAQSectionCta,
  StyledFAQSectionPayTitle,
  StyledFAQSectionWrapper,
  StyledFilteredItemsWrapper,
  StyledTopicFiltersWrapper,
} from '../styledFaqs'
import { StyledAccordionContent, StyledAccordionPanel, StyledAccordionTitle } from './popularQuestions'
import { ContactCta } from '../../../fragments/contactCta'

export const TopContent: React.FC<PropsWithChildren<FaqProps & { category?: string }>> = (props) => {
  const { entry, categories, entries, category } = props
  const router = useRouter()
  const [isOpenAll, setOpenAll] = useState(false)
  const [loading, setLoading] = useState(false)

  const faqCategories = categories ? (nonNull(categories).map(x => {
    if (x && x.__typename === 'categoryFaqs_Category') {
      return {
        value: x.id,
        text: x.title,
        link: x.slug,
      }
    }

    return null
  }) as ({ value: string; text: string; link: string })[]).sort((a, b) => (a.text > b.text) ? 1 : ((b.text > a.text) ? -1 : 0)) : []

  const faqCategorySections = entries ? entries.map(x => {
    if (x && x.__typename === 'faqs_default_Entry') {
      return (
        {
          id: x.id,
          heading: x.title,
          body: x.faqText !== null ? x.faqText : '',
          categoryIds: x.faqCategoryType?.filter(isNotNull).map(f => f.id) ?? [],
          slug: x.slug,
        }
      )
    }

    return null
  }).filter(isNotNull) : []

  const filteredArray: { value: string; text: string; link: string }[] = faqCategories.filter(x => faqCategorySections?.some(y => y?.categoryIds.includes(x.value)))

  const queryCategory = useMemo(() => {
    const category = router.query.category

    return Array.isArray(category) ? category[0] : category
  }, [router])

  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(queryCategory || (filteredArray[0]?.value ?? undefined))

  useEffect(() => {
    if (category) {
      setSelectedCategory(category)
    }
  }, [category])

  useEffect(() => {
    if (queryCategory) {
      setSelectedCategory(queryCategory)
    }
  }, [queryCategory])

  const onChange: TopicFiltersProps['onChangeEvent'] = useCallback((values) => {
    const category = Array.isArray(values) ? values[0] : values || filteredArray[0]?.value

    setSelectedCategory(category)
    setLoading(true)
    setTimeout(() => { setLoading(false) }, 800)
  }, [filteredArray])

  const onClickExpand = (event) => {
    event.stopPropagation()
    setOpenAll(!isOpenAll)
  }

  if (!entry || entry.__typename !== 'faqLandingPage_faqLandingPage_Entry') {
    return null
  }

  return (
    <>
      <StyledTopicFiltersWrapper container direction={'column'} alignItems={'center'}>
        <TopicFilters
          multiple={false}
          filterOptions={filteredArray}
          filterTitle={selectedCategory === null ? 'Filter by - Most Popular' : `Filter by - ${filteredArray.find(x => x.value === selectedCategory)?.text ?? 'Unknown'}`}
          selectedFilters={nonNull([selectedCategory])}
          onChangeEvent={onChange}
          loading={loading}
        />
      </StyledTopicFiltersWrapper>
      <StyledFAQSectionWrapper container direction={'column'} alignItems={'center'} spacing={2}>
        {/** @ts-ignore **/}
        <StyledFAQHeader item lg={10} component={'section'}>
          <Grid container direction={'row'} justifyContent={'space-between'} alignItems={'baseline'}>
            <StyledFAQSectionPayTitle variant="h3" tabIndex={0} $loading={loading}>
              {selectedCategory === null ? 'Most Popular' : faqCategories.find(x => x.value === selectedCategory)?.text}
            </StyledFAQSectionPayTitle>
            <StyledFAQSectionCta text tabIndex={0} icon={isOpenAll ? faMinus : faPlus} onClick={onClickExpand}>
              {isOpenAll ? 'Collapse all' : 'Expand all'}
            </StyledFAQSectionCta>
          </Grid>
        </StyledFAQHeader>
        {/** @ts-ignore **/}
        <StyledFilteredItemsWrapper item lg={10} component={'section'} $loading={loading}>
          {faqCategorySections && (
            <Accordion openAll={isOpenAll}>
              {nonNull(faqCategorySections).filter(c => selectedCategory === null || c.categoryIds.includes(selectedCategory)).map((v, i) => (
                <StyledAccordionPanel panel key={i} tabIndex={0} id={v.slug} onExpand={() => trackFaqView(v.id)}>
                  <StyledAccordionTitle title>{v.heading}</StyledAccordionTitle>
                  {v.body && (
                    <StyledAccordionContent content>{html(v.body)}</StyledAccordionContent>
                  )}
                </StyledAccordionPanel>
              ))}
            </Accordion>
          )}
        </StyledFilteredItemsWrapper>
      </StyledFAQSectionWrapper>
      {/** @ts-ignore **/}
      <StyledContactCtaWrapper item component={'section'}>
        {entry.linksCta && (
          <ContactCta data={nonNull(entry.linksCta)}/>
        )}
      </StyledContactCtaWrapper>
    </>
  )
}